import React, { useCallback, useEffect } from 'react';
import {
    Form, Modal, DatePicker, InputNumber, Typography, Row,
} from 'antd';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import {DATE_FORMAT, convertToUTCDate, getYearExpiry} from '../packageUtil';
import {cancelCreateCredits, requestAddMoreCredit} from "../../../../state/ui/credits/customerCreditsUiActions";
import {
    selectEditState,
    selectCreditBalancesRequest,
    submitCreditAllocationRequest
} from "../../../../state/ui/credits/customerCreditsUiReducer";
import {selectCreditAllocation} from "../../../../state/domain/credits/customerCreditReducer";
import {getDefaultLocale} from "../../../../configWeb";

const { Text } = Typography;

// FIELDS
/**
 * The field for the expiry date
 */
const EndDateField = injectIntl(({
       form, intl, disabled,
     }) => (
    <Form.Item required label={<FormattedMessage id="expiryDate" />}>
      { form.getFieldDecorator('dateExpiry', {
        initialValue: getYearExpiry(moment.utc()),
        rules: [
            { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
          <DatePicker
              style={{ width: '300px' }}
              disabled={disabled}
              disabledDate={value => convertToUTCDate(value).isBefore(moment.utc()) || convertToUTCDate(value).isAfter(getYearExpiry(moment.utc()))}
              format={DATE_FORMAT}
              placeholder={intl.formatMessage({ id: 'selectOne' })}
              showToday={false}
              allowClear={false}
          />,
      )}
    </Form.Item>
));

/**
 * The field for the tokens
 */
const AllocatedTokensField = injectIntl(({
       form, disabled, intl,
     }) => (
    <Form.Item required label={<FormattedMessage id="numberOfCredits" />}>
      { form.getFieldDecorator('tokensAllocated', {
        initialValue: 0,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
          <InputNumber
              disabled={disabled}
              precision={0}
              min={0}
              placeholder={intl.formatMessage({ id: 'enter' })}
          />,
      )}
    </Form.Item>
));

/**
 * Read Only Current Tokens
 */
const ShowBalance = injectIntl(({
 balance, userLocale, intl,
}) => (
    <Form.Item required label={<FormattedMessage id="currentBalance" />}>
        <Text>{balance ? balance.toLocaleString(userLocale) : 0}</Text>
    </Form.Item>
));

// MODAL AND FORM
const CreateCreditAllocation = injectIntl(Form.create({ name: 'create-alloc' })(({
    intl, form, visible, setVisible, creditRow, onCreate, sessionUser,
    onCancel, isLoading, isSubmitting, validationBox, manageCompany, ...rest
}) => {
  const canEditAll = true;
  const { resetFields } = form;

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();
    }
  }, [resetFields, visible]);

  // On form submit.
  const submitForm = useCallback(() => {
    if (!canEditAll) { onCancel(); return; }
    form.validateFields();
    form.validateFields((err, values) => {
      if (!err) {
        const saveRecord = {
          userGroupId: creditRow.id,
          tokenCount: values.tokensAllocated,
          endDate: convertToUTCDate(values.dateExpiry).startOf('day').toISOString(),
          description: creditRow.description,
          currentBalance: creditRow.currentBalance,
        };
        onCreate(saveRecord, manageCompany, intl);
      }
    });
  }, [form, onCreate, intl, onCancel, canEditAll, manageCompany, creditRow]);

  return (
      <Modal
          title={<Row><FormattedMessage id="addCreditsFor" />&nbsp;<Text>{creditRow ? creditRow.description : ''}</Text></Row>}
          okText={<FormattedMessage id="save" />}
          onOk={submitForm}
          visible={visible}
          onCancel={onCancel}
          confirmLoading={isSubmitting}
          {...rest}
      >
        <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
          <ViaValidationAlert validationError={validationBox} />
          <ShowBalance balance={creditRow ? creditRow.currentBalance : 0} userLocale={userLocale} />
          <AllocatedTokensField form={form} disabled={!canEditAll} />
          <EndDateField form={form} disabled={!canEditAll} />
        </Form>
      </Modal>
  );
}));

// Validate the prop type parameters.
CreateCreditAllocation.propTypes = {
  visible: PropTypes.bool.isRequired,
  sessionUser: PropTypes.shape({
    username: PropTypes.string.isRequired,
  }),
  creditRow: PropTypes.shape({
      id: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      currentBalance: PropTypes.number.isRequired,
  }),
  onCreate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
};

CreateCreditAllocation.defaultProps = {
  isLoading: false,
  validationBox: null,
  creditRow: null,
};

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = submitCreditAllocationRequest(state);
  const pageDataRequest = selectCreditBalancesRequest(state);

  return {
    visible: editState.isEditing,
    creditRow: editState.code ? selectCreditAllocation(state, editState.code) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: (newCredit, manageCompany) => dispatch(requestAddMoreCredit(newCredit, manageCompany)),
  onCancel: () => dispatch(cancelCreateCredits()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateCreditAllocation);
